import { Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { selectAllEntities, selectEntity, setEntities, withEntities } from '@ngneat/elf-entities';
import { filter, map, Observable, ReplaySubject, switchMap } from 'rxjs';

export class FeatureFlag {
  guid: string;
  name: FeatureName;
  featureFlagType: 'FrontEnd' | 'BackEnd';
  featureFlagState: 'Disabled' | 'Enabled';
  description?: string;
  betaList?: string;
  betaUserEmails?: string[];
  dateInserted?: string | Date;
  insertedBy?: string;
  dateUpdated?: string | Date;
  updatedBy?: string;
}

export enum FeatureName {
  AIDashboardButtonIntegration = 'AIDashboardButtonIntegration',
  ActiveUserFF = 'ActiveUserFF',
  BNLPhase2 = 'BNLPhase2',
  CCW2 = 'CCW2.0',
  ConditionsPortal = 'ConditionsPortal',
  Disclosures = 'Disclosures',
  DisclosureHistoryV2 = 'DisclosureHistoryV2',
  EmploymentTableFlyoutRedesign = 'EmploymentTableFlyoutRedesign',
  OpportunityOfferHistoryPanel = 'OpportunityOfferHistoryPanel',
  OpportunityMarketingAutomations = 'OpportunityMarketingAutomations',
  OpportunityNonPortfolioLoans = 'OpportunityNonPortfolioLoans',
  OpsPipeline = 'Ops Pipeline',
  SandboxLoanMVP2 = 'SandboxLoanMVP2',
  ScenarioPricing1J2CCW = 'ScenarioPricing1J2CCW',
  ScenarioPricingLegacy = 'ScenarioPricingLegacy',

  ServicingLoanData = 'ServicingLoanData',
  ApplicationConversionNotification = 'ApplicationConversionNotification',
  OpportunityDataEnrichmentPanel = 'OpportunityDataEnrichmentPanel',
  OpportunityCombinedLoansTable = 'OpportunityCombinedLoansTable',
  OpportunityDataEnrichmentOptIn = 'OpportunityDataEnrichmentOptIn',
  AdminScreen = 'AdminScreen',
  NonPortfolioOptInModalFlag = 'NonPortfolioOptInModalFlag',
  ClosedLoan1 = 'ClosedLoan1.0',
  EnableJVPreApproval = 'enableJVNewPreApproval',
  PrintMostCommonDocs = 'PrintMostCommonDocs',
  PartnerCoBranding = 'PartnerCoBranding',
  FeatureFlagManagement = 'FeatureFlagManagement',
  ScenarioPricing1BPrepaymentStructure = 'ScenarioPricing1BPrepaymentStructure',
  ClosedLoansCrossBusinessUnitRestriction = 'ClosedLoansCrossBusinessUnitRestriction',
  LoanNotesV2 = 'LoanNotesV2',
}

const store = createStore({ name: 'featureFlags' }, withEntities<FeatureFlag, 'name'>({ idKey: 'name' }));

@Injectable({ providedIn: 'root' })
export class FeatureFlagRepository {
  private readonly _featureFlagsRetrieved$ = new ReplaySubject<boolean>();
  featureFlags$ = store.pipe(selectAllEntities());

  constructor() {}

  setFeatureFlags(featureFlags: FeatureFlag[]) {
    store.update(setEntities(featureFlags));
    this._featureFlagsRetrieved$.next(true);
  }

  isFeatureEnabled$(name: FeatureName): Observable<boolean> {
    return this._featureFlagsRetrieved$.pipe(
      filter((v) => v),
      switchMap(() =>
        store.pipe(
          selectEntity(name),
          map((flag) => flag?.featureFlagState === 'Enabled'),
        ),
      ),
    );
  }

  isEnabled(name: FeatureName): boolean {
    const state = store.getValue();
    const flag = state.entities[name];
    return flag?.featureFlagState === 'Enabled';
  }

  updateFeatureFlag(featureFlag: FeatureFlag) {
    store.update((state) => ({ ...state, featureFlag }));
  }
}
